import { createContext, use, useId, type ComponentProps, type ReactNode } from "react";
import { Label as LabelPrimitive } from "radix-ui";
import { cn } from "@rekati/utils";

export function Label({ className, ...props }: ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn("text-sm font-medium leading-none text-foreground select-none", className)}
      {...props}
    />
  );
}

const inputBase = [
  "w-full min-w-0 bg-transparent text-foreground placeholder:text-muted-foreground/70",
  "transition-[border-color,box-shadow] duration-200 outline-none",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "aria-invalid:border-destructive",
];

export function Input({ className, ...props }: ComponentProps<"input">) {
  return (
    <input
      data-slot="input"
      className={cn(
        inputBase,
        "h-11 rounded-md border border-input px-3.5 text-[0.95rem]",
        "focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/20",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        inputBase,
        "min-h-24 rounded-md border border-input px-3.5 py-2.5 text-[0.95rem]",
        "focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/20",
        className,
      )}
      {...props}
    />
  );
}

interface FieldCtx {
  id: string;
  hintId: string;
  errorId: string;
  invalid: boolean;
  describedBy: string | undefined;
}
const FieldContext = createContext<FieldCtx | null>(null);

/**
 * Wires a label, a hint and an error to one control by id, so a screen reader
 * hears the hint and the error with the field (WCAG 1.3.1, 3.3.1). Read the
 * ids with `useField()` in a custom control.
 */
export function Field({
  label,
  hint,
  error,
  children,
  className,
  labelClassName,
  optionalLabel,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: (ids: FieldCtx) => ReactNode;
  className?: string;
  labelClassName?: string;
  optionalLabel?: string;
}) {
  const id = useId();
  const ctx: FieldCtx = {
    id,
    hintId: `${id}-hint`,
    errorId: `${id}-error`,
    invalid: Boolean(error),
    describedBy:
      [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined,
  };
  return (
    <FieldContext value={ctx}>
      <div data-slot="field" className={cn("grid gap-2", className)}>
        <Label htmlFor={id} className={labelClassName}>
          {label}
          {optionalLabel ? (
            <span className="ml-1.5 font-normal text-muted-foreground">{optionalLabel}</span>
          ) : null}
        </Label>
        {children(ctx)}
        {hint ? (
          <p id={ctx.hintId} className="text-sm text-muted-foreground">
            {hint}
          </p>
        ) : null}
        <FieldError id={ctx.errorId}>{error}</FieldError>
      </div>
    </FieldContext>
  );
}

export function useField(): FieldCtx | null {
  return use(FieldContext);
}

export function FieldError({
  id,
  children,
  className,
}: {
  id?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <p
      id={id}
      role={children ? "alert" : undefined}
      className={cn(
        "flex min-h-0 items-start gap-1.5 text-sm font-medium text-destructive empty:hidden",
        className,
      )}
    >
      {children}
    </p>
  );
}
